/* eslint-disable class-methods-use-this */
import React from 'react';
import PropTypes from 'prop-types';
import { Header, Container, Grid } from 'semantic-ui-react';
import Call from '../../molecules/Call';
import Email from '../../molecules/Email';
import Location from '../../molecules/Location';
import ContactRail from '../../molecules/ContactRail';
import HoursOfOperation from '../../molecules/HoursOfOperation';
import Image from '../../atoms/Image';
import './Contact.css';
import { isHoursAvailable } from '../../molecules/HoursOfOperation/HoursOfOperation';

export default class Contact extends React.Component {
  // Returns whether you're on a mobile device
  isMobileDevice() {
    if (typeof navigator !== 'undefined') {
      if (
        navigator.userAgent.match(/Android/i) ||
        navigator.userAgent.match(/webOS/i) ||
        navigator.userAgent.match(/iPhone/i) ||
        navigator.userAgent.match(/iPad/i) ||
        navigator.userAgent.match(/iPod/i) ||
        navigator.userAgent.match(/BlackBerry/i) ||
        navigator.userAgent.match(/Windows Phone/i)
      )
        return true;
    }

    return false;
  }

  // Renders hours vertically
  renderHours() {
    const { hours } = this.props;
    return isHoursAvailable(hours) ? (
      <React.Fragment>
        <Header>Hours</Header>
        <HoursOfOperation hours={hours} displayOption="grouped" />
      </React.Fragment>
    ) : (
      <div />
    );
  }

  // Renders a plain map
  renderMap() {
    const { address, businessName } = this.props;
    return <Location address={address} businessName={businessName} showMap showAddress={false} />;
  }

  // Renders phone number, email, address and name as text depending on availability of props
  renderText() {
    const { businessName, phoneNumber, email, address, name } = this.props;

    return (
      <div>
        {phoneNumber && <Call phoneNumber={phoneNumber} />}
        {email && <Email email={email} />}
        {name && (
          <Header className="locationName" as="h4">
            {name}
          </Header>
        )}
        {address && <Location address={address} businessName={businessName} />}
      </div>
    );
  }

  // Integrates text sections into larger components with images and maps
  renderTextSection() {
    const { header, image, showMap, hours, name } = this.props;

    // Hours not included in props
    // Logic: If the hours aren't included, it displays a side block view. If an image
    // is specified, the side block is the image; if not, it's a map.
    if (!hours) {
      return (
        <Grid stackable>
          <Grid.Column className="contact-graphic" width={10}>
            {image === '' ? this.renderMap() : <Image src={image} />}
          </Grid.Column>
          <Grid.Column width={5} verticalAlign="middle">
            {header !== '' && <Header>{header}</Header>}
            {name && <Header>{name}</Header>}
            {this.renderText()}
          </Grid.Column>
        </Grid>
      );
    }

    // Hours included in props
    // Logic: It includes either an image, a map, both, or neither
    // No "neither" case for v1 to remove chances of odd spacing

    // Both an image and a map
    if (showMap && image !== '') {
      return (
        <Grid stackable>
          <Grid.Row columns={2}>
            <Grid.Column className="contact-graphic" width={10}>
              {this.renderMap()}
            </Grid.Column>
            <Grid.Column width={5} verticalAlign="middle">
              {header === '' ? <Header>Contact</Header> : <Header>{header}</Header>}
              {this.renderText()}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={2} verticalAlign="middle">
            <div className="left floated right aligned five wide column hours">
              {this.renderHours()}
            </div>
            <Grid.Column className="contact-graphic" width={10}>
              <Image src={image} centered />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      );
    }

    // Otherwise includes either an image or a map
    return (
      <Grid stackable>
        <Grid.Column className="contact-graphic" width={10}>
          {image === '' ? this.renderMap() : <Image src={image} />}
        </Grid.Column>
        <Grid.Column width={5} verticalAlign="middle" className="contact-content">
          {header === '' ? <Header>{header}</Header> : <Header>Contact</Header>}
          {this.renderText()}
          <br />
          {this.renderHours()}
        </Grid.Column>
      </Grid>
    );
  }

  renderRail() {
    const { phoneNumber, email, address, buttonColor } = this.props;
    const railItems = [];
    const callItem = phoneNumber ? { type: 'call', props: { phoneNumber } } : {};
    const emailItem = email ? { type: 'email', props: { email } } : {};
    const locationItem = address ? { type: 'location', props: { address } } : {};
    railItems.push(callItem, emailItem, locationItem);
    return this.isMobileDevice() ? (
      <ContactRail items={railItems} iconsOnly grouped buttonColor={buttonColor} />
    ) : (
      <ContactRail items={railItems} iconsOnly vertical grouped buttonColor={buttonColor} />
    );
  }

  // Rail of Email, Location, Call depending on what's available
  renderRailSection() {
    const { image } = this.props;

    // Image use case
    return (
      <Grid stackable>
        <Grid.Row centered columns={2}>
          <Grid.Column floated="left" verticalAlign="middle" width={11}>
            {image === '' ? this.renderMap() : <Image src={image} />}
          </Grid.Column>
          <Grid.Column width={5} verticalAlign="middle">
            <Grid.Row verticalAlign="middle">
              {this.renderRail()}
              <p />
              {this.renderHours()}
            </Grid.Row>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }

  render() {
    const { as } = this.props;
    if (as === 'text') {
      return <Container>{this.renderTextSection()}</Container>;
    }
    return <Container>{this.renderRailSection()}</Container>;
  }
}

Contact.propTypes = {
  businessName: PropTypes.string,
  phoneNumber: PropTypes.string,
  email: PropTypes.string,
  address: PropTypes.shape({
    street: PropTypes.string,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired,
    country: PropTypes.string,
  }).isRequired,
  hours: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.number.isRequired,
      open: PropTypes.number.isRequired,
      close: PropTypes.number.isRequired,
      label: PropTypes.string,
    }),
  ),
  as: PropTypes.oneOf(['text', 'rail']),
  buttonColor: PropTypes.string,
  header: PropTypes.string,
  image: PropTypes.string,
  showMap: PropTypes.bool,
};

Contact.defaultProps = {
  businessName: '',
  as: 'text',
  email: '',
  buttonColor: 'default',
  header: '',
  hours: null,
  image: '',
  showMap: false,
  phoneNumber: '',
};

Contact.propOptions = {
  buttonColor: ['primary', 'secondary', 'default'],
  header: ['Contact', 'Contact Us'],
};

Contact.dataPropsMap = {
  address: ['business', 'primary_location'],
  email: ['business', 'primary_location', 'email'],
  phoneNumber: ['business', 'primary_location', 'phone_number'],
  hours: ['business', 'primary_location', 'hours'],
};
